export type CreditDocumentNumberMode = 'auto_derived' | 'manual'

/**
 * Pure, event-driven ownership rules for the debit/credit set's
 * `credit_document_number` (`XMLInsights-SDD` `REQ-TESTDATA-002`). Kept
 * dependency-free (no React imports) so they can be unit-tested without a
 * frontend test framework — see `creditDocumentNumberMode.test.ts` (runs via
 * Node's built-in `node:test`, no new devDependency).
 *
 * `useDebitCreditSet.ts` is the only caller; it owns all React state/refs.
 * These functions never read or compare the field's string value — mode is
 * determined purely by *which event* occurred, never by whether the
 * resulting/current value happens to equal the derived value. That value-
 * equality shortcut was the bug this module replaces: a user edit that
 * coincidentally matched the derived string was previously (mis)classified
 * back as auto-derived, so a later debit change silently overwrote what the
 * user had deliberately typed.
 */

/**
 * A genuine user edit to the credit-number field ALWAYS transitions to
 * `"manual"` — regardless of what value the user typed, including a value
 * that happens to equal the current auto-derived string.
 */
export function nextModeOnUserEdit(): CreditDocumentNumberMode {
  return 'manual'
}

/**
 * A system-driven derivation (the field is recomputed from the debit
 * number) ALWAYS sets/keeps `"auto_derived"`. Never call this from a user
 * input handler.
 */
export function nextModeOnSystemDerive(): CreditDocumentNumberMode {
  return 'auto_derived'
}

/**
 * Reset-trigger rule "manual -> auto_derived": re-enabling the debit/credit
 * set (a new session) resets ownership to auto_derived, regardless of the
 * mode before disabling. Disabling leaves the current mode untouched — the
 * credit field is not visible while disabled, and there is nothing to
 * reset. This is the sole reset-trigger for this requirement; a successful
 * generation deliberately does NOT also reset it (kept to the smallest,
 * single predictable trigger).
 */
export function nextModeOnSetEnabled(
  enabling: boolean,
  currentMode: CreditDocumentNumberMode,
): CreditDocumentNumberMode {
  return enabling ? 'auto_derived' : currentMode
}

/** Creditfactuurnummer afgeleid van het debetfactuurnummer: enige verschil is het
 * prefix (INV- → CN-), de rest (datum/initialen/teller) loopt automatisch mee. */
export function deriveCreditNumber(debitDocumentNumber: string): string {
  return debitDocumentNumber.startsWith('INV-') ? `CN-${debitDocumentNumber.slice(4)}` : debitDocumentNumber
}

/**
 * Reset-consistency helper for re-enabling the set (`setEnabled(true)`).
 *
 * Bugfix: resetting ONLY the ownership ref (mode) to `auto_derived` without
 * also immediately re-deriving the *visible* value left a stale manual
 * string on screen — the `useEffect` that re-derives the display value only
 * re-runs when the debit number itself changes, not when ownership flips.
 * So re-enabling with an unchanged debit number left e.g. "CUSTOM-123"
 * visible while the backend, on submit, would derive `CN-...-04` from the
 * now-auto_derived request — a visible UI value that did not match what was
 * actually generated.
 *
 * This helper returns BOTH the reset mode and the value that must be
 * written to the visible field in the same operation, so the caller cannot
 * apply one without the other.
 */
export function resetCreditDocumentNumberOnEnable(
  debitDocumentNumber: string,
): { mode: CreditDocumentNumberMode; value: string } {
  return {
    mode: nextModeOnSetEnabled(true, 'manual'), // currentMode is irrelevant here: enabling always yields auto_derived
    value: deriveCreditNumber(debitDocumentNumber),
  }
}
